import { useEffect, useState } from "react";
import { Sparkle, MapPin, CaretLeft, Copy, House, Crown, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { coverImage, onPropImgError } from "../lib/helpers";
import { Button } from "./ui/button";

const TONES = [["polished", "Polished"], ["warm", "Warm"], ["punchy", "Punchy"], ["minimal", "Minimal"]];

const SECTIONS = [
  ["mls_description", "MLS Description"],
  ["instagram_caption", "Instagram Caption"],
  ["facebook_post", "Facebook Post"],
  ["email_blast", "Buyer Email Blast"],
  ["open_house_blurb", "Open House Blurb"],
];

function CopyCard({ label, text }) {
  if (!text) return null;
  const copy = () => { navigator.clipboard?.writeText(text); toast.success(`${label} copied`); };
  return (
    <div className="lux-card rounded-[5px] p-4" data-testid={`la-section-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-[#4A0000]">{label}</span>
        <button onClick={copy} data-testid="la-copy-btn" className="flex items-center gap-1 text-xs text-[#5C5A58] hover:text-[#4A0000] transition-colors"><Copy size={14} />Copy</button>
      </div>
      <p className="text-sm text-[#1A1A1A] whitespace-pre-line leading-relaxed">{text}</p>
    </div>
  );
}

function GenerateView({ property, onBack }) {
  const [tone, setTone] = useState("polished");
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client.get(`/listassist/${property.id}`).then((r) => { setContent(r.data.content); if (r.data.tone) setTone(r.data.tone); }).catch(() => {});
  }, [property.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await client.post("/listassist/generate", { property_id: property.id, tone });
      setContent(r.data.content);
      toast.success("Listing copy generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate copy"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="la-generate">
      <button onClick={onBack} data-testid="la-back" className="flex items-center gap-1.5 text-sm text-[#5C5A58] hover:text-[#4A0000] transition-colors">
        <CaretLeft size={16} /> All shoots
      </button>
      <div>
        <h2 className="font-display text-3xl text-[#4A0000] truncate">{property.title}</h2>
        <p className="text-sm text-[#5C5A58] flex items-center gap-1 mt-1"><MapPin size={14} weight="duotone" />{property.address}</p>
      </div>

      <div className="lux-card rounded-[5px] p-5 flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs text-[#5C5A58] mb-1.5">Tone</p>
          <div className="flex gap-1.5" data-testid="la-tone-picker">
            {TONES.map(([v, l]) => (
              <button key={v} data-testid={`la-tone-${v}`} onClick={() => setTone(v)}
                className={`px-3 py-1.5 rounded-md text-sm transition ${tone === v ? "bg-[#4A0000] text-white" : "bg-[#FAF9F6] text-[#5C5A58] hover:bg-[#EEE8DA]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <Button onClick={generate} disabled={busy} data-testid="la-generate-btn"
          className="w-full sm:w-auto sm:ml-auto justify-center bg-[#4A0000] hover:bg-[#5C1A1A] text-white">
          {content ? <ArrowClockwise size={16} className="mr-2" /> : <Sparkle size={16} weight="fill" className="mr-2" />}
          {busy ? "Writing…" : content ? "Regenerate" : "Generate copy"}
        </Button>
      </div>

      {busy && !content && (
        <div className="text-center py-10 text-[#8A6D1B] border border-dashed border-[#E4D08A] rounded-md" data-testid="la-busy">
          <Sparkle size={28} weight="fill" className="mx-auto mb-2 animate-pulse" />
          <p className="text-sm">Crafting your listing copy & social posts…</p>
        </div>
      )}

      {content && (
        <div className="space-y-3" data-testid="la-content">
          {SECTIONS.map(([k, label]) => <CopyCard key={k} label={label} text={content[k]} />)}
          {content.hashtags && content.hashtags.length > 0 && (
            <div className="lux-card rounded-[5px] p-4" data-testid="la-section-hashtags">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#4A0000]">Hashtags</span>
                <button onClick={() => { navigator.clipboard?.writeText(content.hashtags.join(" ")); toast.success("Hashtags copied"); }} data-testid="la-copy-btn" className="flex items-center gap-1 text-xs text-[#5C5A58] hover:text-[#4A0000]"><Copy size={14} />Copy</button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {content.hashtags.map((h, i) => <span key={i} className="text-xs bg-[#B08D57]/12 text-[#8A6D1B] border border-[#B08D57]/30 rounded-full px-2 py-0.5">{h}</span>)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ListAssist() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    client.get("/properties").then((r) => setProperties(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (selected) return <GenerateView property={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="max-w-4xl space-y-6" data-testid="listassist-hub">
      <div>
        <p className="eyebrow text-xs text-[#B08D57] mb-1">Marketing</p>
        <h2 className="font-display text-3xl text-[#4A0000]">ListAssist</h2>
        <p className="text-sm text-[#5C5A58] mt-1">Instant, on-brand listing copy and social posts for any shoot — MLS description, Instagram, Facebook, buyer email and hashtags. A Penthouse Partner perk.</p>
      </div>

      {loading ? (
        <p className="text-sm text-[#5C5A58]">Loading your shoots…</p>
      ) : properties.length === 0 ? (
        <div className="text-center py-16 text-[#5C5A58] border border-dashed border-[#E9DDC6] rounded-md" data-testid="la-empty">
          <House size={40} weight="duotone" className="mx-auto mb-3 text-[#4A0000]" />
          <p>No shoots yet. Book a shoot and generate its listing copy here.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="la-grid">
          {properties.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)} data-testid={`la-card-${p.id}`}
              className="text-left bg-white border border-[#E9DDC6] rounded-[5px] overflow-hidden hover:-translate-y-[2px] hover:shadow-premium-lg hover:border-[#D9CFB0] transition-[transform,box-shadow,border-color] duration-200 group">
              <div className="h-32 bg-[#FAF9F6] overflow-hidden">
                <img src={coverImage(p)} alt={p.title} onError={onPropImgError} className="w-full h-full object-cover transition-transform duration-[600ms] group-hover:scale-[1.05]" />
              </div>
              <div className="p-4">
                <h3 className="font-display text-lg leading-tight line-clamp-1 group-hover:text-[#4A0000] transition-colors">{p.title}</h3>
                <p className="text-xs text-[#5C5A58] flex items-center gap-1 mt-0.5 min-w-0"><MapPin size={12} weight="duotone" className="shrink-0" /><span className="truncate">{p.address}</span></p>
                <span className="inline-flex items-center gap-1 mt-3 text-xs text-[#4A0000] font-medium"><Sparkle size={13} weight="fill" className="text-[#B08D57]" />Generate copy →</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
